import { ArrowRight, ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';

import { FAQ_SECTION, HOME_FAQS } from '@/data/homeContent';
import { cn } from '@/lib/cn';

/**
 * The five questions, the right half of the second two-column row.
 *
 * One answer open at a time, because two open panels push the row taller than
 * the steps and pricing beside it for no gain. Buttons rather than `details`
 * elements: `details` cannot be driven from a parent, so a single-open
 * accordion would have to fight the browser for control of the open state.
 */
export function FaqAccordion() {
  const [open, setOpen] = useState<number | null>(null);
  const baseId = useId();

  return (
    <section
      aria-labelledby="home-faq"
      className="rounded-2xl border border-line/70 bg-white p-4 shadow-card sm:p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <h2 id="home-faq" className="text-lg font-bold tracking-tight text-ink sm:text-xl">
          {FAQ_SECTION.heading}
        </h2>
        <Link
          to={FAQ_SECTION.linkTo}
          className="group inline-flex shrink-0 items-center gap-1 pt-1 text-xs font-bold text-brand-600 transition-colors hover:text-brand-700"
        >
          {FAQ_SECTION.linkLabel}
          <ArrowRight
            className="size-3.5 transition-transform group-hover:translate-x-0.5 rtl-flip"
            aria-hidden
          />
        </Link>
      </div>

      <ul className="mt-4 space-y-2">
        {HOME_FAQS.map((faq, index) => {
          const expanded = open === index;
          const panelId = `${baseId}-panel-${index}`;
          const buttonId = `${baseId}-button-${index}`;

          return (
            <li
              key={faq.question}
              className={cn(
                'rounded-xl border bg-white transition-colors',
                expanded ? 'border-brand-300' : 'border-line',
              )}
            >
              <h3>
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => setOpen(expanded ? null : index)}
                  className="flex min-h-11 w-full items-center justify-between gap-3 px-3.5 py-2.5 text-start text-xs font-medium text-ink transition-colors hover:text-brand-600 sm:text-sm"
                >
                  {faq.question}
                  {/* The circled chevron the design puts at the end of every
                      row. It is inside the button, so it needs no hit area of
                      its own - the whole row is already the target. */}
                  <span
                    className={cn(
                      'grid size-6 shrink-0 place-items-center rounded-full border transition-colors',
                      expanded ? 'border-brand-300 text-brand-600' : 'border-line text-muted',
                    )}
                    aria-hidden
                  >
                    <ChevronRight
                      className={cn(
                        'size-3.5 transition-transform rtl-flip',
                        expanded && 'rotate-90',
                      )}
                    />
                  </span>
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!expanded}
                className="px-3.5 pb-3"
              >
                <p className="hyphens-auto text-justify text-xs leading-relaxed text-muted">
                  {faq.answer}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
